import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SitemapJsonLd } from "../components/seo/JsonLd";
import { useI18n } from "../i18n/I18nProvider";
import { CITIES, SERVICE_AREAS, ALTERNATIVES, SEO_SOLUTIONS, getServiceArea, getSeoSolution } from "../lib/seoLanding";

const SECTION_DEFS = [
  {
    id: "discover",
    title_key: "sec_discover",
    links: [
      { to: "/", key: "lbl_home" },
      { to: "/why", key: "lbl_why" },
      { to: "/about", key: "lbl_about" },
      { to: "/industries", key: "lbl_industries_all" },
      { to: "/case-studies", key: "lbl_case_studies" },
      { to: "/blog", key: "lbl_blog" },
      { to: "/compare/lightspeed", key: "lbl_vs_ls" },
    ],
  },
  {
    id: "product",
    title_key: "sec_product",
    links: [
      { to: "/solutions/pos", key: "lbl_pos" },
      { to: "/solutions/kiosk", key: "lbl_kiosk" },
      { to: "/solutions/waiter-app", key: "lbl_waiter" },
      { to: "/solutions/qr-order-pay", key: "lbl_qr" },
      { to: "/solutions/online-shop", key: "lbl_shop" },
      { to: "/solutions/payment-terminals", key: "lbl_terminals" },
      { to: "/solutions/gift-cards-loyalty", key: "lbl_loyalty" },
      { to: "/solutions", key: "lbl_all_products" },
    ],
  },
  {
    id: "industries",
    title_key: "sec_industries",
    links: [
      { to: "/industries#quick-service", key: "lbl_qsr" },
      { to: "/industries#delivery-service", key: "lbl_delivery" },
      { to: "/industries#restaurants", key: "lbl_restaurants" },
      { to: "/industries#grocery-stores", key: "lbl_grocery" },
      { to: "/industries#food-truck", key: "lbl_foodtruck" },
      { to: "/industries#franchise", key: "lbl_franchise" },
    ],
  },
  {
    id: "pricing-tools",
    title_key: "sec_pricing_tools",
    links: [
      { to: "/pricing", key: "lbl_pricing" },
      { to: "/offers", key: "lbl_offers" },
      { to: "/configurator", key: "lbl_configurator" },
      { to: "/payments", key: "lbl_payments" },
    ],
  },
  {
    id: "cities",
    title_key: "sec_cities",
    links: CITIES.map((c) => ({ to: `/pos/${c.slug}`, label_template: "pos", city_name: c.name })),
  },
  {
    id: "service-areas",
    title_key: "sec_service_areas",
    links: SERVICE_AREAS.map((a) => ({ to: `/service-areas/${a.slug}`, area_slug: a.slug })),
  },
  {
    id: "alternatives",
    title_key: "sec_alternatives",
    links: ALTERNATIVES.map((a) => ({ to: `/alternatives/${a.slug}`, label_template: "alt", competitor: a.competitor })),
  },
  {
    id: "seo-solutions",
    title_key: "sec_seo_solutions",
    links: [
      ...SEO_SOLUTIONS.map((s) => ({ to: `/${s.slug}`, seo_slug: s.slug })),
    ],
  },
  {
    id: "contact",
    title_key: "sec_contact",
    links: [
      { to: "/contact", key: "lbl_contact" },
      { to: "/callback", key: "lbl_callback" },
      { to: "/#demo", key: "lbl_book_demo" },
    ],
  },
  {
    id: "legal",
    title_key: "sec_legal",
    links: [
      { to: "/legal/imprint", key: "lbl_imprint" },
      { to: "/legal/privacy", key: "lbl_privacy" },
      { to: "/legal/terms", key: "lbl_terms" },
      { to: "/sitemap", key: "lbl_sitemap" },
    ],
  },
];

export default function Sitemap() {
  const { t, locale } = useI18n();

  const renderLinkLabel = (l) => {
    if (l.key) return t(`sitemap_page.${l.key}`);
    if (l.label_template === "pos") return `${t("sitemap_page.lbl_pos_in")} ${l.city_name}`;
    if (l.label_template === "alt") return locale === "fr" ? `${t("sitemap_page.lbl_alt_suffix")} ${l.competitor}` : `${l.competitor} ${t("sitemap_page.lbl_alt_suffix")}`;
    if (l.area_slug) return getServiceArea(l.area_slug, locale)?.name || l.area_slug;
    if (l.seo_slug) return getSeoSolution(l.seo_slug, locale)?.eyebrow || l.seo_slug;
    return l.label;
  };

  return (
    <div data-testid="sitemap-page">
      <SitemapJsonLd />
      <section className="py-24 sm:py-28 border-b border-zinc-200 relative overflow-hidden">
        <div className="absolute inset-0 technical-grid pointer-events-none" />
        <div className="container-oj relative">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">{t("sitemap_page.eyebrow")}</div>
          <h1 className="font-display mt-4 text-balance text-5xl sm:text-6xl font-black tracking-tighter leading-[0.95] text-oj-ink max-w-3xl">
            {t("sitemap_page.h1")}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-zinc-600">
            {t("sitemap_page.intro")}
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white">
        <div className="container-oj grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {SECTION_DEFS.map((s) => (
            <div key={s.id} data-testid={`sitemap-section-${s.id}`} className="rounded-2xl border border-zinc-200 p-7 bg-zinc-50/60">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">
                {s.title_key ? t(`sitemap_page.${s.title_key}`) : s.title}
              </div>
              <ul className="mt-4 space-y-2.5">
                {s.links.map((l) => (
                  <li key={l.to + (l.key || l.label || l.area_slug || l.seo_slug || l.competitor || l.city_name)}>
                    <Link to={l.to} className="group inline-flex items-center gap-1.5 text-zinc-800 hover:text-oj-ink font-medium">
                      {renderLinkLabel(l)}
                      <ArrowUpRight className="size-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
